import { formatMoney as formatMoneyWithFormat } from '@theme/money-formatting';

/**
 * Pay-Per-Cleanse® payment plan calculator.
 *
 * Port of the legacy WP/WooCommerce "page-paypercleanse.php" template.
 * Behavior preserved from the legacy version:
 *  - Only the Premier/Solo package quantities are real cart line items.
 *  - The "monthly payments" and "overnight repair service" rows are never
 *    added to the cart as their own line items — they're informational
 *    only, attached to the package line item as cart properties (the
 *    Shopify equivalent of WooCommerce's per-cart-item custom data), and
 *    do not affect what's charged at checkout.
 *  - Monthly qty always mirrors its package qty.
 *  - Repair qty can never exceed its package qty.
 *  - Both agreement checkboxes are required before adding to cart.
 */
class PpcPaymentPlan extends HTMLElement {
  connectedCallback() {
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.cartAddUrl = this.dataset.cartAddUrl;
    this.cartUrl = this.dataset.cartUrl;
    this.repairPrice = Number(this.dataset.repairPrice) || 0;

    this.premier = {
      qtyInput: this.querySelector('[data-ppc-qty="premier"]'),
      repairInput: this.querySelector('[data-ppc-repair-qty="premier"]'),
      monthlyQtyEl: this.querySelector('[data-ppc-monthly-qty="premier"]'),
      totalEl: this.querySelector('[data-ppc-total="premier"]'),
      monthlyTotalEl: this.querySelector('[data-ppc-total="premier-monthly"]'),
      repairTotalEl: this.querySelector('[data-ppc-total="premier-repair"]'),
      variantId: this.dataset.premierVariantId,
      price: Number(this.dataset.premierPrice) || 0,
      monthlyPrice: Number(this.dataset.premierMonthlyPrice) || 0,
    };

    this.solo = {
      qtyInput: this.querySelector('[data-ppc-qty="solo"]'),
      repairInput: this.querySelector('[data-ppc-repair-qty="solo"]'),
      monthlyQtyEl: this.querySelector('[data-ppc-monthly-qty="solo"]'),
      totalEl: this.querySelector('[data-ppc-total="solo"]'),
      monthlyTotalEl: this.querySelector('[data-ppc-total="solo-monthly"]'),
      repairTotalEl: this.querySelector('[data-ppc-total="solo-repair"]'),
      variantId: this.dataset.soloVariantId,
      price: Number(this.dataset.soloPrice) || 0,
      monthlyPrice: Number(this.dataset.soloMonthlyPrice) || 0,
    };

    this.grandTotalEl = this.querySelector('[data-ppc-grand-total]');
    this.monthlyGrandTotalEl = this.querySelector('[data-ppc-monthly-grand-total]');
    this.agreeInputs = this.querySelectorAll('[data-ppc-agree]');
    this.statusEl = this.querySelector('[data-ppc-status]');
    this.submitButton = this.querySelector('[data-ppc-submit]');
    this.submitLabel = this.submitButton ? this.submitButton.textContent : 'Add to cart';

    [this.premier.qtyInput, this.premier.repairInput, this.solo.qtyInput, this.solo.repairInput].forEach((input) => {
      if (input) input.addEventListener('input', () => this.recalculate());
    });

    this.submitButton?.addEventListener('click', (event) => {
      event.preventDefault();
      this.handleSubmit();
    });

    this.zoomController = new AbortController();
    this.initZoom(this.zoomController.signal);

    // The browser restores the qty inputs when navigating back (bfcache) but
    // does NOT fire `input`, so the totals would stay at $0. Recalculate on
    // pageshow (fires on load and on bfcache restore) to re-sync them.
    this.onPageShow = () => this.recalculate();
    window.addEventListener('pageshow', this.onPageShow);

    this.recalculate();
  }

  disconnectedCallback() {
    this.zoomController?.abort();
    if (this.onPageShow) {
      window.removeEventListener('pageshow', this.onPageShow);
    }
  }

  /**
   * Package photo lightbox — matches the legacy WP theme's shared image
   * lightbox (see .amd-product-pdp__zoom in sections/product-pdp.liquid):
   * a plain <dialog> that fades in/out, with a top-right toolbar holding a
   * zoom toggle and a close button (mirroring Fancybox's default toolbar,
   * which the legacy WP page used via data-fancybox).
   */
  initZoom(signal) {
    const dialog = this.querySelector('[data-ppc-zoom-dialog]');
    const image = this.querySelector('[data-ppc-zoom-image]');
    const closeButton = this.querySelector('[data-ppc-zoom-close]');
    const scaleButton = this.querySelector('[data-ppc-zoom-scale]');
    const triggers = this.querySelectorAll('[data-ppc-zoom]');
    if (!dialog || !image || !triggers.length || typeof dialog.showModal !== 'function') return;

    const openDialog = () => {
      dialog.showModal();
      window.requestAnimationFrame(() => dialog.classList.add('is-open'));
    };

    const closeDialog = () => {
      if (!dialog.open) return;
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        dialog.close();
      };
      dialog.classList.remove('is-open');
      dialog.addEventListener('transitionend', finish, { once: true });
      window.setTimeout(finish, 400);
    };

    triggers.forEach((trigger) => {
      trigger.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          image.src = trigger.getAttribute('href');
          image.alt = trigger.getAttribute('data-zoom-alt') || '';
          resetZoom();
          openDialog();
        },
        { signal: signal }
      );
    });

    // The wrap is 785x785 (cropped) by default. Zooming lifts that limit on
    // both the image and its wrap (see .is-zoomed in pay-per-cleanse.css) so
    // the box grows to the image's natural size — capped by the viewport —
    // instead of clipping a bigger image inside a fixed window.
    const wrap = image.parentElement;

    const resetZoom = () => {
      image.classList.remove('is-zoomed');
      wrap.classList.remove('is-zoomed');
    };

    const toggleZoom = () => {
      image.classList.toggle('is-zoomed');
      wrap.classList.toggle('is-zoomed');
    };

    if (scaleButton) scaleButton.addEventListener('click', toggleZoom, { signal: signal });
    image.addEventListener('click', toggleZoom, { signal: signal });

    if (closeButton) closeButton.addEventListener('click', closeDialog, { signal: signal });

    dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === dialog) closeDialog();
      },
      { signal: signal }
    );

    dialog.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault();
        closeDialog();
      },
      { signal: signal }
    );

    dialog.addEventListener(
      'close',
      () => {
        image.removeAttribute('src');
        resetZoom();
      },
      { signal: signal }
    );
  }

  // money_format can use any of Shopify's amount placeholders — this store
  // uses amount_with_comma_separator — and can carry markup, so defer to the
  // theme's helper and strip tags, since the result goes into textContent.
  formatMoney(cents) {
    const format = this.moneyFormat.replace(/<[^>]*>/g, '');
    const currency = window.Shopify?.currency?.active || 'USD';

    return formatMoneyWithFormat(cents, format, currency);
  }

  recalculate() {
    const premierQty = Math.max(0, parseInt(this.premier.qtyInput && this.premier.qtyInput.value, 10) || 0);
    const soloQty = Math.max(0, parseInt(this.solo.qtyInput && this.solo.qtyInput.value, 10) || 0);

    let premierRepairQty = Math.max(0, parseInt(this.premier.repairInput && this.premier.repairInput.value, 10) || 0);
    let soloRepairQty = Math.max(0, parseInt(this.solo.repairInput && this.solo.repairInput.value, 10) || 0);

    // Repair qty can never exceed the package qty it belongs to.
    if (premierRepairQty > premierQty) premierRepairQty = premierQty;
    if (soloRepairQty > soloQty) soloRepairQty = soloQty;
    if (this.premier.repairInput) this.premier.repairInput.value = premierRepairQty;
    if (this.solo.repairInput) this.solo.repairInput.value = soloRepairQty;

    // Monthly qty always mirrors the package qty.
    if (this.premier.monthlyQtyEl) this.premier.monthlyQtyEl.textContent = premierQty;
    if (this.solo.monthlyQtyEl) this.solo.monthlyQtyEl.textContent = soloQty;

    const premierLineTotal = premierQty * this.premier.price;
    const soloLineTotal = soloQty * this.solo.price;
    const premierMonthlyTotal = premierQty * this.premier.monthlyPrice;
    const soloMonthlyTotal = soloQty * this.solo.monthlyPrice;
    const premierRepairTotal = premierRepairQty * this.repairPrice;
    const soloRepairTotal = soloRepairQty * this.repairPrice;

    if (this.premier.totalEl) this.premier.totalEl.textContent = this.formatMoney(premierLineTotal);
    if (this.solo.totalEl) this.solo.totalEl.textContent = this.formatMoney(soloLineTotal);
    if (this.premier.monthlyTotalEl) this.premier.monthlyTotalEl.textContent = this.formatMoney(premierMonthlyTotal);
    if (this.solo.monthlyTotalEl) this.solo.monthlyTotalEl.textContent = this.formatMoney(soloMonthlyTotal);
    if (this.premier.repairTotalEl) this.premier.repairTotalEl.textContent = this.formatMoney(premierRepairTotal);
    if (this.solo.repairTotalEl) this.solo.repairTotalEl.textContent = this.formatMoney(soloRepairTotal);

    const grandTotal = premierLineTotal + soloLineTotal;
    const monthlyGrandTotal = premierMonthlyTotal + soloMonthlyTotal + premierRepairTotal + soloRepairTotal;

    if (this.grandTotalEl) this.grandTotalEl.textContent = this.formatMoney(grandTotal);
    if (this.monthlyGrandTotalEl) this.monthlyGrandTotalEl.textContent = this.formatMoney(monthlyGrandTotal);

    this.state = {
      premierQty,
      soloQty,
      premierRepairQty,
      soloRepairQty,
      premierMonthlyTotal,
      soloMonthlyTotal,
      premierRepairTotal,
      soloRepairTotal,
    };
  }

  showStatus(message, variant) {
    if (!this.statusEl) return;
    if (!message) {
      this.statusEl.hidden = true;
      this.statusEl.textContent = '';
      this.statusEl.className = 'ppc-plan__status';
      return;
    }
    this.statusEl.hidden = false;
    this.statusEl.textContent = message;
    this.statusEl.className = 'ppc-plan__status ppc-plan__status--' + variant;
  }

  handleSubmit() {
    this.recalculate();
    const state = this.state;
    const errors = [];

    const bothAgreed = Array.prototype.every.call(this.agreeInputs, (input) => input.checked);
    if (!bothAgreed) errors.push('Please agree to the Terms & Conditions and Refunds Policy.');

    if (state.premierQty === 0 && state.soloQty === 0) {
      errors.push('Please select a quantity for at least one package.');
    }

    if (errors.length) {
      this.showStatus(errors.join(' '), 'error');
      return;
    }

    const items = [];
    const configErrors = [];

    if (state.premierQty > 0) {
      if (!this.premier.variantId) {
        configErrors.push('The Premier package isn\'t linked to a product — check the section settings.');
      } else {
        const properties = {};
        if (state.premierRepairQty > 0) properties['Overnight Repair Service Qty'] = String(state.premierRepairQty);
        properties['Monthly Payment Total'] = this.formatMoney(state.premierMonthlyTotal + state.premierRepairTotal);
        items.push({ id: Number(this.premier.variantId), quantity: state.premierQty, properties: properties });
      }
    }

    if (state.soloQty > 0) {
      if (!this.solo.variantId) {
        configErrors.push('The Solo package isn\'t linked to a product — check the section settings.');
      } else {
        const properties = {};
        if (state.soloRepairQty > 0) properties['Overnight Repair Service Qty'] = String(state.soloRepairQty);
        properties['Monthly Payment Total'] = this.formatMoney(state.soloMonthlyTotal + state.soloRepairTotal);
        items.push({ id: Number(this.solo.variantId), quantity: state.soloQty, properties: properties });
      }
    }

    if (configErrors.length) {
      this.showStatus(configErrors.join(' '), 'error');
      return;
    }

    if (!items.length) {
      this.showStatus('Please select a quantity for at least one package.', 'error');
      return;
    }

    if (this.submitButton) {
      this.submitButton.disabled = true;
      this.submitButton.textContent = 'Adding...';
    }
    this.showStatus('', null);

    fetch(this.cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data: data })))
      .then((result) => {
        if (!result.ok) {
          throw new Error((result.data && result.data.description) || 'Add to cart failed');
        }
        window.location.href = this.cartUrl;
      })
      .catch(() => {
        this.showStatus('Something went wrong adding these packages to your cart. Please try again.', 'error');
        if (this.submitButton) {
          this.submitButton.disabled = false;
          this.submitButton.textContent = this.submitLabel;
        }
      });
  }
}

if (!customElements.get('ppc-payment-plan')) {
  customElements.define('ppc-payment-plan', PpcPaymentPlan);
}
